/**
 * How a board names a reading (sweep PR-E, 2026-10-24: legends, axes and table columns printed the tag's name,
 * "pour.pour_temperature_first", two of them run together). The words come from the server, never from a table here:
 * the tag list carries each tag's label and machine (GET /v1/iot/tags, from the function the machine page names a port
 * with), and use-live-tag-data puts them on each reading. A reading from an older server, with no label, prints its
 * tag's name as before.
 */
import type { TwinAggregates } from "./twin-aggregates";
import type { LiveTagData, LiveTagDatum } from "./use-live-tag-data";

type Named = Pick<LiveTagDatum, "name" | "label" | "machine" | "unit">;

/** A reading's own name: its label in the plant's words, else its tag's name. Pure. */
export function readingName(d: Pick<LiveTagDatum, "name" | "label">): string {
  return d.label?.trim() || d.name;
}

/**
 * The names of the readings one chart draws, in their order: each its label, and where readings in one unit come from
 * more than one machine, each of those takes its machine in brackets ("Moisture (Sand Mixer)"), so two lines on one
 * axis say whose each is. Readings that would still read the same take their tags' names. Pure.
 */
export function chartReadingNames(ds: readonly Named[]): string[] {
  const machinesByUnit = new Map<string, Set<string>>();
  for (const d of ds) {
    const unit = d.unit ?? "";
    const set = machinesByUnit.get(unit) ?? new Set<string>();
    set.add(d.machine?.trim() ?? "");
    machinesByUnit.set(unit, set);
  }
  const names = ds.map((d) => {
    const own = readingName(d);
    const machine = d.machine?.trim();
    const mixed = (machinesByUnit.get(d.unit ?? "")?.size ?? 0) > 1;
    return mixed && machine ? `${own} (${machine})` : own;
  });
  const count = new Map<string, number>();
  for (const n of names) count.set(n.toLowerCase(), (count.get(n.toLowerCase()) ?? 0) + 1);
  return names.map((n, i) => ((count.get(n.toLowerCase()) ?? 0) > 1 && ds[i].name ? ds[i].name : n));
}

const CLOCK: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
};

/**
 * A reading's time on the shop floor's clock, to the minute ("22 Oct 2026, 14:05"): the plant's time zone when it is
 * known, else the browser's. A time that does not parse is shown as given. Pure.
 */
export function plantClockText(iso: string, zone?: string | null): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat("en-GB", zone ? { ...CLOCK, timeZone: zone } : CLOCK).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-GB", CLOCK).format(d);
  }
}

/**
 * What an empty reading widget says, in the plant's words (sweep PR-E, D3): a reading the plant has, with nothing in the
 * widget's period, says so; a reading the plant does not have yet says the chart is not connected. Never the builder's
 * "bind a tag" instruction. Pure.
 */
export function noReadingsWords(
  liveTagData: LiveTagData | undefined,
  keys: readonly string[],
): { message: string; hint?: string } {
  const found = keys.some((k) => k && liveTagData?.[k.toLowerCase()] !== undefined);
  return found
    ? { message: "No readings in this period" }
    : { message: "No data", hint: "The reading this chart shows is not connected yet." };
}

/**
 * Why a scrap widget is empty, in the plant's words (sweep PR-E, D3), true in each case. A plant read from its casting
 * lots (lotsAnswer: lots in the window, and no heats of its own or only imported ones) whose lots hold nothing cast has
 * no rate to draw; a plant whose heats are all imported, with no lot in the window, has no scrap lot booked; a plant
 * with neither heats nor lots in the period is told so; a plant whose heats carry no scrap is told that. Never "run
 * heats". Pure.
 */
export function noScrapWords(
  heats: Pick<TwinAggregates, "heatCount" | "allHeatsImported">,
  lots = 0,
): string {
  if (lots > 0 && (heats.heatCount === 0 || heats.allHeatsImported)) return "No castings were made in this period.";
  if (heats.allHeatsImported) return "No scrap lots are booked in this period.";
  if (heats.heatCount === 0) return "No heats or casting lots in this period.";
  return "No scrap is recorded on the heats in this period.";
}
